import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingCart, Check, RefreshCw, Loader2, Apple, Carrot, Milk, 
  Beef, Wheat, Coffee, Nut, ChevronDown, ChevronUp, Printer, 
  Share2, Calendar, CheckCircle2, Circle, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CATEGORY_INFO = {
  legume: { name: 'Legume', icon: Carrot, color: 'bg-green-500' },
  fructe: { name: 'Fructe', icon: Apple, color: 'bg-red-500' },
  lactate: { name: 'Lactate', icon: Milk, color: 'bg-blue-400' },
  carne_și_pește: { name: 'Carne și Pește', icon: Beef, color: 'bg-red-700' },
  cereale_și_paste: { name: 'Cereale și Paste', icon: Wheat, color: 'bg-amber-600' },
  leguminoase: { name: 'Leguminoase', icon: Coffee, color: 'bg-orange-600' },
  nuci_și_semințe: { name: 'Nuci și Semințe', icon: Nut, color: 'bg-amber-800' },
  uleiuri_și_condimente: { name: 'Uleiuri și Condimente', icon: Coffee, color: 'bg-yellow-600' },
  altele: { name: 'Altele', icon: ShoppingCart, color: 'bg-gray-500' }
};

const ShoppingList = () => {
  const { API } = useAuth();
  const [shoppingList, setShoppingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [togglingItem, setTogglingItem] = useState(null);

  useEffect(() => {
    fetchShoppingList();
  }, []);

  const fetchShoppingList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shopping-lists/current`);
      setShoppingList(response.data);
      // Expand all categories by default
      const expanded = {};
      Object.keys(response.data.categories || {}).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      if (error.response?.status === 404) {
        // No active meal plan
        setShoppingList(null);
      } else {
        console.error('Error fetching shopping list:', error);
        toast.error('Eroare la încărcarea listei de cumpărături');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!shoppingList?.meal_plan_id) {
      toast.error('Nu există un plan de mese activ');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/shopping-lists/generate`, {
        meal_plan_id: shoppingList.meal_plan_id
      });
      setShoppingList(response.data);
      toast.success('Lista a fost regenerată!');
    } catch (error) {
      toast.error('Eroare la regenerarea listei');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleItem = async (category, itemName) => {
    if (!shoppingList?.id) return;
    
    setTogglingItem(`${category}-${itemName}`);
    try {
      await axios.put(`${API}/shopping-lists/${shoppingList.id}/toggle-item`, {
        category,
        item_name: itemName
      });
      
      // Update local state
      setShoppingList(prev => {
        const newCategories = { ...prev.categories };
        const items = newCategories[category].map(item => 
          item.name.toLowerCase() === itemName.toLowerCase()
            ? { ...item, checked: !item.checked }
            : item
        );
        newCategories[category] = items;
        
        const totalChecked = Object.values(newCategories).reduce(
          (sum, items) => sum + items.filter(i => i.checked).length,
          0
        );
        
        return { ...prev, categories: newCategories, checked_items: totalChecked };
      });
    } catch (error) {
      toast.error('Eroare la actualizarea itemului');
    } finally {
      setTogglingItem(null);
    }
  };

  const handleResetList = async () => {
    if (!shoppingList?.id) return;
    
    try {
      await axios.put(`${API}/shopping-lists/${shoppingList.id}/reset`);
      setShoppingList(prev => ({
        ...prev,
        categories: Object.fromEntries(
          Object.entries(prev.categories).map(([cat, items]) => [
            cat,
            items.map(item => ({ ...item, checked: false }))
          ])
        ),
        checked_items: 0
      }));
      toast.success('Lista a fost resetată!');
    } catch (error) {
      toast.error('Eroare la resetarea listei');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const progress = shoppingList 
    ? Math.round((shoppingList.checked_items / shoppingList.total_items) * 100) || 0
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex flex-col gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#1A4D2E] mb-1 sm:mb-2">
                Lista de Cumpărături
              </h1>
              <p className="text-[#4a5568] text-sm sm:text-base">
                Bazată pe planul tău alimentar săptămânal
              </p>
            </div>
            
            <div className="flex gap-2 print:hidden">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="flex-1 sm:flex-none text-xs sm:text-sm"
              >
                <Printer className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Printează</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegenerate}
                disabled={generating || !shoppingList}
                className="flex-1 sm:flex-none text-xs sm:text-sm"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 sm:mr-2" />
                )}
                <span className="hidden sm:inline">Regenerează</span>
              </Button>
            </div>
          </div>

          {!shoppingList ? (
            <Card className="border-none card-shadow">
              <CardContent className="p-12 text-center">
                <ShoppingCart className="w-16 h-16 mx-auto text-[#1A4D2E]/20 mb-4" />
                <h3 className="text-xl font-medium text-[#1A4D2E] mb-2">
                  Nu ai încă o listă de cumpărături
                </h3>
                <p className="text-[#4a5568] mb-6">
                  Generează mai întâi un plan de mese pentru a crea automat lista de cumpărături.
                </p>
                <Button
                  onClick={() => window.location.href = '/meal-plans'}
                  className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                >
                  Mergi la Planuri de Mese
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Progress Card */}
              <Card className="border-none card-shadow mb-4 sm:mb-6">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#1A4D2E] rounded-full flex items-center justify-center">
                        <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xl sm:text-2xl font-bold text-[#1A4D2E]">
                          {shoppingList.checked_items} / {shoppingList.total_items}
                        </p>
                        <p className="text-xs sm:text-sm text-[#4a5568]">ingrediente cumpărate</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                      <Badge className="bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs">
                        <Calendar className="w-3 h-3 mr-1" />
                        {shoppingList.days_covered || 7} zile
                      </Badge>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetList}
                        className="text-[#4a5568] print:hidden text-xs sm:text-sm"
                      >
                        <RotateCcw className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Reset</span>
                      </Button>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full h-2 sm:h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[#1A4D2E] to-[#2E7D4A]"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <p className="text-center text-xs sm:text-sm text-[#4a5568] mt-2">
                    {progress}% completat
                  </p>
                </CardContent>
              </Card>

              {/* Categories */}
              <div className="space-y-3 sm:space-y-4">
                {Object.entries(shoppingList.categories || {}).map(([categoryKey, items]) => {
                  const categoryInfo = CATEGORY_INFO[categoryKey] || CATEGORY_INFO.altele;
                  const IconComponent = categoryInfo.icon;
                  const checkedInCategory = items.filter(i => i.checked).length;
                  const isExpanded = expandedCategories[categoryKey];
                  
                  return (
                    <Card key={categoryKey} className="border-none card-shadow overflow-hidden">
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(categoryKey)}
                        className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-[#F5F5F0] transition-colors"
                      >
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 ${categoryInfo.color} rounded-lg flex items-center justify-center`}>
                            <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-[#1A4D2E] text-sm sm:text-base">{categoryInfo.name}</p>
                            <p className="text-xs sm:text-sm text-[#4a5568]">
                              {checkedInCategory}/{items.length} cumpărate
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {checkedInCategory === items.length && items.length > 0 && (
                            <Badge className="bg-green-100 text-green-700 text-xs hidden sm:flex">
                              <Check className="w-3 h-3 mr-1" />
                              Complet
                            </Badge>
                          )}
                          {checkedInCategory === items.length && items.length > 0 && (
                            <Check className="w-4 h-4 text-green-600 sm:hidden" />
                          )}
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-[#4a5568]" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#4a5568]" />
                          )}
                        </div>
                      </button>
                      
                      {/* Items */}
                      {isExpanded && (
                        <CardContent className="p-0">
                          <div className="divide-y divide-[#E2E8F0]">
                            {items.map((item, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleToggleItem(categoryKey, item.name)}
                                disabled={togglingItem === `${categoryKey}-${item.name}`}
                                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between hover:bg-[#F5F5F0] transition-all active:bg-[#E2E8F0] ${
                                  item.checked ? 'bg-green-50' : ''
                                }`}
                              >
                                <div className="flex items-center gap-2 sm:gap-3">
                                  {togglingItem === `${categoryKey}-${item.name}` ? (
                                    <Loader2 className="w-5 h-5 animate-spin text-[#1A4D2E]" />
                                  ) : item.checked ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Circle className="w-5 h-5 text-[#CBD5E0]" />
                                  )}
                                  <span className={`text-sm sm:text-base ${item.checked ? 'line-through text-[#718096]' : 'text-[#1A4D2E]'}`}>
                                    {item.name}
                                  </span>
                                </div>
                                <span className={`text-xs sm:text-sm ${item.checked ? 'text-[#A0AEC0]' : 'text-[#4a5568]'}`}>
                                  {item.quantity}
                                </span>
                              </button>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>

              {/* Print Footer */}
              <div className="hidden print:block mt-8 pt-4 border-t border-gray-300">
                <p className="text-sm text-gray-500 text-center">
                  NutriHealth - Lista de cumpărături generată pe {new Date().toLocaleDateString('ro-RO')}
                </p>
              </div>
            </>
          )}
        </motion.div>
      </main>
      
      {/* Print Styles */}
      <style>{`
        @media print {
          nav, .print\\:hidden { display: none !important; }
          body { background: white !important; }
          .card-shadow { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
        }
      `}</style>
    </div>
  );
};

export default ShoppingList;
